/**
 * The browser as a sign-in method (docs/identity/ADR-001 §2).
 *
 * - Account key: 32 random bytes kept in localStorage and a first-party
 *   cookie. It is the ONLY thing that opens an account by device on the web;
 *   clearing the browser loses it, which is why a web purchase requires a
 *   sign-in method right after payment.
 * - Hardware key: a fingerprint of stable browser signals. Not unique (two
 *   identical phones collide), so the server uses it only to count the free
 *   grant, never to open an account.
 */

const ACCOUNT_KEY = 'device_account_key';
const SIGNED_OUT_KEY = 'device_signed_out';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string): void {
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax; Secure`;
}

function randomKey(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** Get or create the account key; either store restores the other. */
export function getAccountKey(): string {
  let key: string | null = null;
  try {
    key = localStorage.getItem(ACCOUNT_KEY);
  } catch {
    key = null;
  }
  key = key || readCookie(ACCOUNT_KEY) || randomKey();
  try {
    localStorage.setItem(ACCOUNT_KEY, key);
  } catch {
    // Private mode: the cookie still carries it.
  }
  writeCookie(ACCOUNT_KEY, key);
  return key;
}

/** SHA-256 over stable browser signals; null when crypto.subtle is missing. */
export async function getFingerprint(): Promise<string | null> {
  if (typeof crypto === 'undefined' || !crypto.subtle) return null;

  let canvasSignal = '';
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('GetFacade', 2, 2);
      canvasSignal = canvas.toDataURL();
    }
  } catch {
    canvasSignal = '';
  }

  const signals = [
    navigator.userAgent,
    navigator.language,
    navigator.hardwareConcurrency,
    screen.width,
    screen.height,
    screen.colorDepth,
    window.devicePixelRatio,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    canvasSignal,
  ].join('|');

  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(signals));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Set by an explicit sign-out: the next visit shows the sign-in screen
 * instead of reopening the account by device. Any sign-in clears it.
 */
export function setSignedOut(value: boolean): void {
  try {
    if (value) localStorage.setItem(SIGNED_OUT_KEY, '1');
    else localStorage.removeItem(SIGNED_OUT_KEY);
  } catch {
    // Storage unavailable: nothing to remember.
  }
}

export function mayOpenAccountByDevice(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(SIGNED_OUT_KEY) !== '1';
  } catch {
    return true;
  }
}

export async function deviceSignInBody(locale: string | null): Promise<Record<string, unknown>> {
  return {
    platform: 'web',
    account_key: getAccountKey(),
    hardware_key: await getFingerprint(),
    app_version: process.env.NEXT_PUBLIC_APP_VERSION ?? 'web',
    device_name: 'web',
    locale,
  };
}

/**
 * After a sign-in on the sign-in screen: this browser opens the account next
 * time without asking. Best effort; a failure only means the screen shows
 * again after the storage is cleared.
 */
export function registerBrowserAfterSignIn(locale: string | null): void {
  setSignedOut(false);
  void (async () => {
    try {
      const { authService } = await import('../api/auth');
      await authService.attachDevice(await deviceSignInBody(locale));
    } catch {
      // Ignored on purpose, see above.
    }
  })();
}
